/**
 * 24-hour tick labels for a uPlot time axis.
 *
 * uPlot's default formats hours as "6am / 9am / 12pm". Every other time in the
 * product is 24-hour — the cursor readout on these same charts, the Updated column
 * on the fleet grid, the Alerts window footer — so the axis was the one place that
 * disagreed with itself, and "12pm" directly under a tooltip reading "12:00" is
 * noise in the densest part of the chart.
 *
 * The format follows the tick INCREMENT uPlot chose, not the window: a chart whose
 * ticks step by hours wants a clock, one stepping by days wants a date, and the
 * increment is the only thing that knows which. A day boundary inside an intraday
 * axis still prints its date, because "00:00" alone cannot say which midnight.
 */
import type uPlot from "uplot";

const DAY_S = 86_400;

/**
 * Clamp a reserved day window's RIGHT edge to where the data actually ends.
 *
 * A generation day is framed 06:00–19:00 so the night hours don't eat the plot
 * width. But a LIVE or partial day only reaches the current time, and reserving
 * the whole window then strands the curve in the left third of the plot
 * (UX-REV-002 2.1). Keep the left edge (mornings are never clipped) and pull the
 * right edge in to the last sample plus a small headroom — never past the day's
 * end, and never below a minimum span so a single early reading still frames.
 *
 * `times` are unix SECONDS, ascending, matching `scales.x.time = true`.
 */
export function clampLiveDayWindow(
  start: number,
  end: number,
  times: number[],
  headroomS = 1_800,
): [number, number] {
  const last = times.length ? times[times.length - 1] : undefined;
  if (last === undefined) return [start, end];
  const MIN_SPAN_S = 2 * 3_600;
  const right = Math.min(end, last + headroomS);
  return [start, Math.max(right, start + MIN_SPAN_S)];
}

function clock(d: Date): string {
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function day(d: Date): string {
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * Pass as an axis's `values`. Seconds-based, matching `scales.x.time = true`.
 */
export const timeAxisValues: uPlot.Axis.Values = (_u, splits, _axisIdx, _space, incr) => {
  const daily = (incr ?? 0) >= DAY_S;
  return splits.map((v) => {
    if (v == null) return "";
    const d = new Date(v * 1000);
    if (daily) return day(d);
    // Midnight inside an intraday axis: "00:00" does not say which midnight, and a
    // range spanning two days would print the same label twice.
    if (d.getHours() === 0 && d.getMinutes() === 0) return day(d);
    return clock(d);
  });
};
